import { LocalDateTime } from "@js-joda/core";
import { AnimatePresence, LayoutGroup } from "framer-motion";
import React from "react";
import { Page } from "../../activities/AppPage";
import { VisitInstanceId } from "../../messages";
import {
  CaregiverBankInformation,
  EligibleInstaPayVisitsData,
  InstaPayVisit,
} from "../../messages/Payroll";
import { STRINGS } from "../../strings";
import { AppCaregiver } from "../../system/AppState/AppCaregiverState";
import { translate } from "../../translate";
import { HashSet } from "../../utils/HashSet";
import { CalendarWeekIcon } from "../../utils/icones";
import { AppCard, AppCardContent, AppCardHeader } from "../Generic/AppCard";
import AppSlideFadeIn from "../Generic/AppSlideFadeIn";
import Flex from "../Generic/Flex";
import { VisitBlocks, VisitItem, VisitsList } from "../InstaPayHistory/InstaPayHistoryScreen";
import Margin from "../Margin";
import ScaffoldTitle from "../Scaffold/components/ScaffoldTitle";
import Scaffold from "../Scaffold/Scaffold";
import PayrollButton from "../SelectInstaPayVisits/components/PayrollButton";
import SelectEligibleVisitsCard from "../SelectInstaPayVisits/components/SelectEligibleVisitsCard";
import InstaPayCard from "./components/InstaPayCard";
import useInstaPayVisitItemClickHandler from "./hooks/useInstaPayVisitItemClickHandler";

type EligibleInstaPayVisit = InstaPayVisit & {
  status: {
    type: "EligibleInstaPay";
    eligibleAmount: number;
  };
};

interface Props {
  data: EligibleInstaPayVisitsData;
  visits: InstaPayVisit[];
  caregiver: AppCaregiver;
  onClickContinue: (params: {
    visits: HashSet<InstaPayVisit>;
    bankInformation: CaregiverBankInformation | null;
  }) => void;
  onRequestNavigate: (page: Page) => void;
  onClickGoBack: () => void;
}

export default function InstPayScreen(props: Props) {
  const eligibleVisits = props.data.visits;
  const eligibleBalanceInDollars = (props.data.balance / 100).toFixed(2);
  const [selectedVisitIds, setSelectedVisitIds] = React.useState<HashSet<VisitInstanceId>>(() =>
    HashSet.fromCollection(eligibleVisits.map((x) => x.id))
  );
  const selectedVisits = HashSet.fromCollection(
    eligibleVisits.filter((x) => selectedVisitIds.has(x.id))
  );

  const handleCheckVisit = (visit: Pick<EligibleInstaPayVisit, "id">) => {
    setSelectedVisitIds((prev) => {
      return prev.has(visit.id) ? prev.delete(visit.id) : prev.add(visit.id);
    });
  };

  const handleClickContinue = () => {
    return props.onClickContinue({
      visits: selectedVisits,
      bankInformation: props.data.bankInformation,
    });
  };

  const handleClickVisitItem = useInstaPayVisitItemClickHandler({
    caregiver: props.caregiver,
    currentWeek: 0,
    onRequestNavigate: () => props.onRequestNavigate({ type: "InstaPayHistory" }),
  });

  return (
    <Scaffold
      title={<ScaffoldTitle label={translate(STRINGS.INSTAPAY)} />}
      endActions={
        <CalendarWeekIcon
          onClick={() => props.onRequestNavigate({ type: "InstaPayHistory" })}
          fontSize={20}
        />
      }
      footer={<PayrollButton selectedVisits={selectedVisits} onClick={handleClickContinue} />}
      onClickGoBack={props.onClickGoBack}
    >
      <Margin m={16}>
        <Flex direction="column" gap={16}>
          <InstaPayCard
            totalAmount={`$${eligibleBalanceInDollars}`}
            totalHours={props.data.totalHours}
            totalVisits={props.data.totalVisits}
          />

          <AppSlideFadeIn delay={350}>
            <SelectEligibleVisitsCard
              visits={eligibleVisits}
              selected={selectedVisits}
              onChange={handleCheckVisit}
            />
          </AppSlideFadeIn>

          <AppSlideFadeIn delay={400}>
            <AppCard>
              <AppCardHeader>{translate(STRINGS.OTHER_VISITS)}</AppCardHeader>
              <AppCardContent>
                <VisitBlocks>
                  <LayoutGroup>
                    <AnimatePresence>
                      <VisitsList>
                        {props.visits.map((visit) => (
                          <VisitItem
                            {...visit}
                            key={`${visit.id}`}
                            payRate={((visit.payRate ?? 0) / 100).toFixed(2)}
                            isOpacity={visit.startTime.isAfter(LocalDateTime.now())}
                            onClick={() => handleClickVisitItem(visit)}
                          />
                        ))}
                      </VisitsList>
                    </AnimatePresence>
                  </LayoutGroup>
                </VisitBlocks>
              </AppCardContent>
            </AppCard>
          </AppSlideFadeIn>
        </Flex>
      </Margin>
    </Scaffold>
  );
}
